import Vue from "vue";
import { createAuth0Client } from "@auth0/auth0-spa-js";

const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

let instance;

export const getInstance = () => instance;

export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = window.location.origin,
  ...options
}) => {
  if (instance) return instance;

  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        popupOpen: false,
        error: null
      };
    },
    methods: {
      async loginWithPopup(options, config) {
        this.popupOpen = true;

        try {
          await this.auth0Client.loginWithPopup(options, config);
          this.user = await this.auth0Client.getUser();
          this.isAuthenticated = await this.auth0Client.isAuthenticated();
          this.error = null;
        } catch (e) {
          console.error(e);
          this.error = e;
        } finally {
          this.popupOpen = false;
        }
      },
      async handleRedirectCallback() {
        this.loading = true;
        try {
          await this.auth0Client.handleRedirectCallback();
          this.user = await this.auth0Client.getUser();
          this.isAuthenticated = true;
          this.error = null;
        } catch (e) {
          this.error = e;
        } finally {
          this.loading = false;
        }
      },
      loginWithRedirect(o) {
        if (o) {
          const { appState, authorizationParams, ...rest } = o;
          return this.auth0Client.loginWithRedirect({
            appState,
            authorizationParams: {
              redirect_uri: redirectUri,
              ...authorizationParams,
              ...rest
            }
          });
        }
        return this.auth0Client.loginWithRedirect({
          authorizationParams: {
            redirect_uri: redirectUri
          }
        });
      },
      getIdTokenClaims(o) {
        return this.auth0Client.getIdTokenClaims(o);
      },
      getTokenSilently(o) {
        if (o) {
          const { audience, ...rest } = o;
          return this.auth0Client.getTokenSilently({
            authorizationParams: {
              audience,
              ...rest.authorizationParams,
              ...rest
            }
          });
        }
        return this.auth0Client.getTokenSilently();
      },
      getTokenWithPopup(o) {
        return this.auth0Client.getTokenWithPopup(o);
      },
      logout(o) {
        if (o) {
          const { returnTo, ...rest } = o;
          return this.auth0Client.logout({
            logoutParams: {
              returnTo,
              ...rest.logoutParams,
              ...rest
            }
          });
        }
        return this.auth0Client.logout({
          logoutParams: {
            returnTo: window.location.origin
          }
        });
      }
    },
    async created() {
      const { domain, clientId, ...rest } = options;
      try {
        this.auth0Client = await createAuth0Client({
          domain,
          clientId,
          authorizationParams: {
            redirect_uri: redirectUri,
            ...rest.authorizationParams
          },
          ...rest
        });

        const hasCode = window.location.search.includes("code=");
        const hasState = window.location.search.includes("state=");
        const hasError = window.location.search.includes("error=");

        // Only handle callback automatically if NOT on the manual callback path
        if ((hasCode || hasError) && hasState && window.location.pathname !== "/authentication/auth0-callback") {
          console.log("Auth0: Processing callback...");
          const { appState } = await this.auth0Client.handleRedirectCallback();
          this.error = null;
          console.log("Auth0: Callback processed successfully.");
          onRedirectCallback(appState);
        }
      } catch (e) {
        console.error("Auth0: Error during initialization or callback processing", e);
        this.error = e;
      } finally {
        this.isAuthenticated = await this.auth0Client.isAuthenticated();
        this.user = await this.auth0Client.getUser();
        this.loading = false;
        console.log("Auth0: Loading complete. Authenticated:", this.isAuthenticated);
      }
    }
  });

  return instance;
};

export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth0(options);
  }
};
